import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { trackPageView } from "./lib/pixel";
import { initGA, gaPageView } from "./lib/ga";
import { Toaster } from "sonner";

import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import AuthCallback from "@/components/AuthCallback";
import CookieNotice from "@/components/CookieNotice";

import Landing from "@/pages/Landing";
import LandingV2 from "@/pages/LandingV2";
import AuthPage, { ForgotPassword, ResetPassword, PendingPayment } from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import Decoration from "@/pages/Decoration";
import Ceia from "@/pages/Ceia";
import Recipes from "@/pages/Recipes";
import RecipeDetail from "@/pages/RecipeDetail";
import TableSetting from "@/pages/TableSetting";
import Planner from "@/pages/Planner";
import Gifts from "@/pages/Gifts";
import Moment from "@/pages/Moment";
import Account from "@/pages/Account";
import Onboarding from "@/pages/Onboarding";
import CreateChristmas from "@/pages/CreateChristmas";
import World from "@/pages/World";
import Budget from "@/pages/Budget";
import Admin, { AdminAnalytics, AdminSales, AdminUsersPage, AdminFeedbackPage, AdminSettings, AdminAlertsPage } from "@/pages/Admin";
import AdminLayout from "@/components/AdminLayout";
import SecretSantaList, { SecretSantaCreate, SecretSantaJoin } from "@/pages/SecretSanta";
import SecretSantaReveal from "@/pages/SecretSantaReveal";
import SecretSantaConfirm from "@/pages/SecretSantaConfirm";
import SecretSantaGroup from "@/pages/SecretSantaGroup";
import ThankYou from "@/pages/ThankYou";
import PaymentSuccess from "@/pages/PaymentSuccess";
import RefundPolicy from "@/pages/RefundPolicy";
import LegalPage from "@/pages/LegalPage";
import { termsCopy } from "@/i18n/termsCopy";
import { privacyCopy } from "@/i18n/privacyCopy";
import DemoIntro from "@/demo/DemoIntro";
import DemoEnd from "@/demo/DemoEnd";
import DemoShell, { DemoLockedRedirect } from "@/demo/DemoShell";
import DemoSearch from "@/demo/DemoSearch";
import DemoRecipeDetail from "@/demo/DemoRecipeDetail";

const BANNER_SIZES = ["728x90", "468x60", "336x280", "300x250", "250x250", "160x600"];
function BannerRedirect({ size }) {
  React.useEffect(() => { window.location.replace(`/${size}.png`); }, [size]);
  return null;
}

function LegacyAdminRedirect() {
  // /app/admin[/...] (old URL) → /admin[/...]: same panel, same guards. /admin is the canonical admin URL.
  const { pathname, search } = useLocation();
  return <Navigate to={pathname.replace(/^\/app\/admin/, "/admin") + search} replace />;
}

function AppRoutes() {
  const location = useLocation();
  useEffect(() => { initGA(); }, []);
  useEffect(() => { trackPageView(location.pathname); gaPageView(location.pathname); }, [location.pathname]);
  // Google OAuth returns to /app#session_id=... — handle it before anything else.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<LandingV2 />} />
      <Route path="/v2" element={<Navigate to="/" replace />} />
      <Route path="/v1" element={<Landing />} />
      <Route path="/br" element={<LandingV2 forcedMarket="BR" initialLang="pt" />} />
      <Route path="/us" element={<LandingV2 forcedMarket="US" initialLang="en" />} />
      <Route path="/pt" element={<LandingV2 forcedMarket="PT" initialLang="pt-PT" />} />
      <Route path="/fr" element={<LandingV2 forcedMarket="FR" initialLang="fr" />} />
      <Route path="/es" element={<LandingV2 forcedMarket="ES" initialLang="es" />} />
      <Route path="/it" element={<LandingV2 forcedMarket="IT" initialLang="it" />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/activate" element={<ResetPassword activate />} />
      <Route path="/pending" element={<PendingPayment />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/terms" element={<LegalPage copy={termsCopy} slug="terms" testid="terms-page" />} />
      <Route path="/privacy" element={<LegalPage copy={privacyCopy} slug="privacy" testid="privacy-page" />} />
      <Route path="/obrigado" element={<ThankYou />} />
      <Route path="/thank-you" element={<ThankYou />} />
      <Route path="/secret-santa/join/:token" element={<SecretSantaJoin />} />
      <Route path="/secret-santa/reveal/:token" element={<SecretSantaReveal />} />
      <Route path="/secret-santa/confirm/:token" element={<SecretSantaConfirm />} />
      {BANNER_SIZES.map((s) => (
        <Route key={s} path={`/${s}`} element={<BannerRedirect size={s} />} />
      ))}

      <Route path="/demo" element={<DemoIntro />} />
      <Route path="/demo/end" element={<DemoEnd />} />
      <Route path="/demo/app" element={<DemoShell />}>
        <Route index element={<Dashboard />} />
        <Route path="create" element={<DemoLockedRedirect action="plan_edit" />} />
        <Route path="decoration" element={<Decoration />} />
        <Route path="ceia" element={<Ceia />} />
        <Route path="recipes" element={<Recipes />} />
        <Route path="recipes/:id" element={<DemoRecipeDetail />} />
        <Route path="table" element={<TableSetting />} />
        <Route path="planner" element={<Planner />} />
        <Route path="gifts" element={<Gifts />} />
        <Route path="budget" element={<Budget />} />
        <Route path="secret-santa" element={<SecretSantaList />} />
        <Route path="secret-santa/new" element={<SecretSantaCreate />} />
        <Route path="secret-santa/:groupId" element={<SecretSantaGroup />} />
        <Route path="search" element={<DemoSearch />} />
        <Route path="*" element={<Navigate to="/demo/app" replace />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Admin />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="sales" element={<AdminSales />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="alerts" element={<AdminAlertsPage />} />
          <Route path="feedback" element={<AdminFeedbackPage />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
        <Route path="/app/onboarding" element={<Onboarding />} />
        <Route path="/app/create" element={<CreateChristmas />} />
        <Route path="/app" element={<AppShell />}>
          <Route index element={<Dashboard />} />
          <Route path="world" element={<World />} />
          <Route path="decoration" element={<Decoration />} />
          <Route path="ceia" element={<Ceia />} />
          <Route path="recipes" element={<Recipes />} />
          <Route path="recipes/:id" element={<RecipeDetail />} />
          <Route path="table" element={<TableSetting />} />
          <Route path="planner" element={<Planner />} />
          <Route path="gifts" element={<Gifts />} />
          <Route path="budget" element={<Budget />} />
          <Route path="secret-santa" element={<SecretSantaList />} />
          <Route path="secret-santa/new" element={<SecretSantaCreate />} />
          <Route path="secret-santa/:groupId" element={<SecretSantaGroup />} />
          <Route path="moment" element={<Moment />} />
          <Route path="account" element={<Account />} />
          <Route path="admin/*" element={<LegacyAdminRedirect />} />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <CookieNotice />
          <Toaster position="top-center" theme="dark" richColors />
        </AuthProvider>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
